import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { FormProvider, useForm } from 'react-hook-form';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    Tooltip,
    Theme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { AxiosError } from 'axios';

import FormSelectField from '../../components/formFields/FormSelectField';
import ViewCommonButton from '../../components/buttons/ViewCommonButton';
import Loader from '../../components/Loader';
import { StatusChip } from '../../components/StatusChip';
import TablePaginationLocalContainer from '../../components/Table/TablePaginationLocalContainer';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants/constants';
import { statusOptions, AutoSweepTransactionStatusMap } from './constants';
import useSnackBar from '../../hooks/useSnackBar';
import {
    getAutoSweepTransactionList,
    getAutoSweepBatchDetails,
    exportAutoSweepTransactions,
    retryTransaction,
} from '../../api/bankingApi/bankingApi';
import SweepFailureDetailPopup from '../../components/SweepFailureDetailPopup';
import { AutoSweepStatus, AutoSweepTransaction } from './types';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { useHasPermission } from '../../hooks/useHasPermission';
import { formatDate } from '../../helpers/formatDate';
import { ErrorData } from '../../types';

interface AutoSweepTransactionsTableProps {
    cardId: string;
    accountType?: 'REV' | 'EXP' | string | undefined | null;
}

const useStyles = makeStyles()((theme: Theme) => ({
    transactionsToolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '18px',
        gap: '16px',
    },
    cardSubHeading: {
        fontSize: '16px',
        fontWeight: 500,
        fontFamily: 'Poppins',
    },
    tableContainer: {
        background: 'transparent',
        border: `1px solid ${theme.palette.secondary.dark}`,
        borderRadius: '8px',
        overflow: 'auto',
        fontFamily: 'Poppins',
    },
    tableHeaderCell: {
        background: theme.palette.primary.light,

        '& .MuiTableCell-root': {
            textAlign: 'left',
            color: theme.palette.text.secondary,
            borderBottom: `1px solid ${theme.palette.secondary.dark}`,
            fontWeight: 500,
            fontFamily: 'Poppins',
            fontSize: '14px',
            padding: '14px 16px',
        },
    },
    tableBodyCell: {
        '& .MuiTableCell-root': {
            textAlign: 'left',
            color: theme.palette.common.white,
            borderBottom: `1px solid ${theme.palette.secondary.dark}`,
            fontWeight: 400,
            fontFamily: 'Poppins',
            fontSize: '14px',
            padding: '16px',
        },
    },
    retryIcon: {
        cursor: 'pointer',
        color: theme.palette.info.main,

        '&:hover': {
            opacity: 0.75,
        },
    },
    batchInfoIcon: {
        color: theme.palette.common.white,
    },
    noDataText: {
        fontFamily: 'Poppins',
        color: theme.palette.text.secondary,
        fontSize: '14px',
    },
    batchDialogPaper: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.common.white,
        borderRadius: 16,
    },
    batchDialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        fontFamily: 'Poppins',
        fontWeight: 600,
        color: theme.palette.common.white,
    },
    exportButton: {
        fontFamily: 'Poppins',
        fontSize: '13px',
        fontWeight: 500,
        textTransform: 'none',
        color: theme.palette.common.white,
        borderColor: theme.palette.secondary.dark,

        '&:hover': {
            borderColor: theme.palette.secondary.dark,
            opacity: 0.85,
        },
    },
    batchTotalText: {
        fontFamily: 'Poppins',
        fontSize: '16px',
        color: theme.palette.text.disabled,
        marginBottom: '10px',
    },
    batchToolbar: {
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    batchSearchField: {
        minWidth: '200px',
        flexGrow: 1,
        '& .MuiInputBase-root': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.dark,
            borderRadius: '8px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.dark,
        },
    },
    batchTableContainer: {
        background: 'transparent',
        border: `1px solid ${theme.palette.secondary.dark}`,
        borderRadius: '8px',
        overflow: 'auto',
        fontFamily: 'Poppins',
        maxHeight: '360px',

        '& .MuiTableCell-stickyHeader': {
            backgroundColor: theme.palette.primary.light,
        },
    },
}));

const AutoSweepTransactionsTable = ({
    cardId,
    accountType,
}: AutoSweepTransactionsTableProps) => {
    const { setAlert } = useSnackBar();
    const { classes } = useStyles();
    const navigate = useNavigate();
    const firmId = localStorage.getItem('firmId');
    const canEdit = useHasPermission('Banking', 'Edit');

    const [transactions, setTransactions] = useState<AutoSweepTransaction[]>([]);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
    const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
    const [isFailurePopupOpen, setIsFailurePopupOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<AutoSweepTransaction | null>(null);
    const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
    const [batchDetails, setBatchDetails] = useState<Awaited<ReturnType<typeof getAutoSweepBatchDetails>>['data'] | null>(null);
    const [isBatchPopupOpen, setIsBatchPopupOpen] = useState(false);
    const [batchSearch, setBatchSearch] = useState('');
    const [batchSort, setBatchSort] = useState<'dateDesc' | 'dateAsc' | 'amountDesc' | 'amountAsc'>('dateDesc');
    const [batchPage, setBatchPage] = useState(0);
    const [batchRowsPerPage, setBatchRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
    const [isBatchLoading, setIsBatchLoading] = useState(false);

    const filterFormMethods = useForm({
        defaultValues: {
            [`${cardId}Status`]: 'all',
        },
    });

    const statusFilter = filterFormMethods.watch(`${cardId}Status`);

    const loadTransactions = () => {
        if (!firmId || !accountType) {
            setTransactions([]);
            setTotalTransactions(0);
            return;
        }

        const statusParam =
            statusFilter && statusFilter !== 'all'
                ? (statusFilter as AutoSweepStatus)
                : undefined;

            setIsTransactionsLoading(true);
        getAutoSweepTransactionList({
            page: page + 1,
            size: rowsPerPage,
            accountType: accountType as 'REV' | 'EXP',
            status: statusParam,
            firmId: Number(firmId),
        })
            .then((res) => {
                setTransactions(res.data.items || []);
                setTotalTransactions(res.data.total || 0);
            })
            .catch((error) => {
                setAlert((prev) => ({
                    ...prev,
                    message:
                        error.response?.data?.message || 'Failed to load transactions.',
                    type: 'error',
                }));
            })
            .finally(() => setIsTransactionsLoading(false));
    };

    useEffect(() => {
        loadTransactions();
    }, [firmId, accountType, page, rowsPerPage, statusFilter]);

    useEffect(() => {
        setPage(0);
    }, [accountType, statusFilter]);

    const handleRetry = (transactionId: number) => {
        retryTransaction(Number(firmId), Number(transactionId))
            .then((res) => {
                setAlert((prev) => ({
                    ...prev,
                    message: res.data?.message || 'Transaction retry has been scheduled successfully',
                    type: 'success',
                }));

                loadTransactions();
            })
            .catch((error) => {
                setAlert((prev) => ({
                    ...prev,
                    message:
                        error.response?.data?.message || 'Error. Something went wrong...',
                    type: 'error',
                }));
            });
    };

    const loadBatchDetails = async (batchCode: string, pageNumber: number, pageSize: number, search: string, sort: typeof batchSort) => {
        if (!firmId) return;
        setIsBatchLoading(true);
        try {
            const response = await getAutoSweepBatchDetails(
                Number(firmId),
                batchCode,
                pageNumber,
                pageSize,
                search,
                undefined,
                sort,
            );
            // Some endpoints wrap the payload in a `data` envelope; support both shapes.
            const responseData = response.data as unknown as { data?: typeof response.data };
            const batchData = responseData?.data ?? response.data;
            setSelectedBatch(batchCode);
            setBatchDetails(batchData);
            setIsBatchPopupOpen(true);
        } catch (error) {
            const axiosError = error as AxiosError<ErrorData>;
            setAlert((prev) => ({
                ...prev,
                message: axiosError.response?.data?.message || 'Failed to load batch details.',
                type: 'error',
            }));
        } finally {
            setIsBatchLoading(false);
        }
    };

    const handleOpenBatchPopup = (batchCode: string) => {
        navigate(`/banking/accounts/autosweep/batches/${encodeURIComponent(batchCode)}`);
    };

    useEffect(() => {
        if (isBatchPopupOpen && selectedBatch) {
            loadBatchDetails(selectedBatch, batchPage + 1, batchRowsPerPage, batchSearch, batchSort);
        }
    }, [isBatchPopupOpen, selectedBatch, batchPage, batchRowsPerPage, batchSearch, batchSort]);

    const getContributionType = (contribution: { attyFeeId: number | null; deductionId: number | null }) => {
        if (contribution.attyFeeId != null) return 'Attorney Fee';
        if (contribution.deductionId != null) return 'Deduction';
        return 'Settlement';
    };

    const paginatedContributions = batchDetails?.contributions || [];

    const toggleBatchSort = (column: 'date' | 'amount') => {
        setBatchSort((currentSort) => {
            if (column === 'date') {
                return currentSort === 'dateDesc' ? 'dateAsc' : 'dateDesc';
            }
            return currentSort === 'amountDesc' ? 'amountAsc' : 'amountDesc';
        });
        setBatchPage(0);
    };

    const handleExportTransactions = async () => {
        if (!firmId || !accountType) return;
        try {
            const response = await exportAutoSweepTransactions({
                firmId: Number(firmId),
                accountType,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                batchCode: selectedBatch || undefined,
                search: selectedBatch ? batchSearch : undefined,
                contributionSort: selectedBatch ? batchSort : undefined,
            });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = selectedBatch
                ? `autosweep-batch-${selectedBatch}-transactions.csv`
                : `autosweep-transactions-${accountType}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            const axiosError = error as AxiosError<ErrorData>;
            setAlert((prev) => ({
                ...prev,
                message: axiosError.response?.data?.message || 'Failed to export transactions.',
                type: 'error',
            }));
        }
    };

    const handleOpenFailurePopup = (transaction: AutoSweepTransaction) => {
        setSelectedTransaction(transaction);
        setIsFailurePopupOpen(true);
    };

    const handleCloseFailurePopup = () => {
        setIsFailurePopupOpen(false);
        setSelectedTransaction(null);
    };

    const handleRetryFromPopup = () => {
        if (!selectedTransaction?.id) return;

        handleRetry(selectedTransaction.id);
        handleCloseFailurePopup();
    };

    const shouldShowRetryIcon = (status?: string | null) =>
        ['Failed', 'RetryFailed'].includes(status || '');

    return (
        <>
            <Box className={classes.transactionsToolbar}>
                <Typography className={classes.cardSubHeading}>Recent Sweeps</Typography>

                <Box display="flex" alignItems="center" gap={2}>
                    <FormProvider {...filterFormMethods}>
                        <FormSelectField
                            name={`${cardId}Status`}
                            label="Status"
                            options={statusOptions}
                            minWidth="175px"
                        />
                    </FormProvider>
                    <ViewCommonButton type="export" onClick={handleExportTransactions} />
                </Box>
            </Box>

            <TableContainer
                className={classes.tableContainer}
                component={Paper}
                elevation={0}
            >
                <Table>
                    <TableHead className={classes.tableHeaderCell}>
                        <TableRow>
                            <TableCell>Updated</TableCell>
                            <TableCell>Plaintiff Name</TableCell>
                            <TableCell>Case Reference ID</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Destination</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody className={classes.tableBodyCell}>
                        {isTransactionsLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ height: 180 }}>
                                    <Loader colorType="warning" size={40} />
                                </TableCell>
                            </TableRow>
                        ) : transactions.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    align="center"
                                    sx={{ textAlign: 'center !important' }}
                                >
                                    <Typography className={classes.noDataText}>
                                        No transaction records found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((transaction) => (
                                <TableRow key={`${cardId}-${transaction.id}`}>
                                    <TableCell> {transaction.updatedAt ? formatDate(transaction.updatedAt) : '-'} </TableCell>
                                    <TableCell> {transaction.plaintiffName || '-'} </TableCell>
                                    <TableCell> {transaction.caseReferenceId || '-'} </TableCell>
                                    <TableCell> {numberToUSDCurrency(transaction.amount)} </TableCell>
                                    <TableCell> {transaction.destinationAccountName} </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} alignItems="center">
                                            {transaction.status && (
                                                <StatusChip
                                                    label={AutoSweepTransactionStatusMap[transaction.status]?.label || transaction.status}
                                                    color={AutoSweepTransactionStatusMap[transaction.status]?.color || 'default'}
                                                />
                                            )}
                                            {transaction.batchCode && transaction.status !== 'Cancelled' && (
                                                <Tooltip title="View batch contributions">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`View batch ${transaction.batchCode}`}
                                                        onClick={() => transaction.batchCode && handleOpenBatchPopup(transaction.batchCode)}
                                                    >
                                                        <OpenInNewIcon fontSize="small" className={classes.batchInfoIcon} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Box display="flex" gap={1} alignItems="center">
                                            {shouldShowRetryIcon(transaction.status) && canEdit && (
                                                <RefreshIcon
                                                    className={classes.retryIcon}
                                                    onClick={() => handleOpenFailurePopup(transaction)}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePaginationLocalContainer
                count={totalTransactions}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value as string, 10));
                    setPage(0);
                }}
                totalPages={Math.ceil(totalTransactions / rowsPerPage)}
            />

            <SweepFailureDetailPopup
                open={isFailurePopupOpen}
                onClose={handleCloseFailurePopup}
                firmId={firmId ? Number(firmId) : undefined}
                onRetrySweep={handleRetryFromPopup}
                transactionId={selectedTransaction?.id}
            />

            <Dialog
                open={isBatchPopupOpen}
                onClose={() => setIsBatchPopupOpen(false)}
                fullWidth
                maxWidth="md"
                classes={{ paper: classes.batchDialogPaper }}
            >
                <DialogTitle className={classes.batchDialogTitle}>
                    <span>Autosweep batch {selectedBatch}</span>

                    <ViewCommonButton type="export" onClick={handleExportTransactions} />
                </DialogTitle>
                <DialogContent>
                    <Typography className={classes.batchTotalText}>
                        Total: {batchDetails ? numberToUSDCurrency(batchDetails.amount) : '-'}
                    </Typography>

                    <Box className={classes.batchToolbar}>
                        <TextField
                            className={classes.batchSearchField}
                            size="small"
                            placeholder="Search by case reference ID"
                            value={batchSearch}
                            onChange={(e) => {
                                setBatchSearch(e.target.value);
                                setBatchPage(0);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon htmlColor="#C3C4C8" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <TableContainer className={classes.batchTableContainer} component={Paper} elevation={0}>
                        <Table stickyHeader>
                            <TableHead className={classes.tableHeaderCell}>
                                <TableRow>
                                    <TableCell>Case Reference ID</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={batchSort === 'amountDesc' || batchSort === 'amountAsc'}
                                            direction={batchSort === 'amountAsc' ? 'asc' : 'desc'}
                                            onClick={() => toggleBatchSort('amount')}
                                        >
                                            Amount
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody className={classes.tableBodyCell}>
                                {isBatchLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ height: 180 }}>
                                            <Loader colorType="warning" size={40} />
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedContributions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ textAlign: 'center !important' }}>
                                            <Typography className={classes.noDataText}>
                                                No contributions found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedContributions.map((contribution, index) => (
                                        <TableRow key={`${contribution.settlementId}-${index}`}>
                                            <TableCell>{contribution.caseReferenceId || '-'}</TableCell>
                                            <TableCell>{getContributionType(contribution)}</TableCell>
                                            <TableCell>{numberToUSDCurrency(contribution.amount)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePaginationLocalContainer
                        count={batchDetails?.total || 0}
                        page={batchPage}
                        rowsPerPage={batchRowsPerPage}
                        onPageChange={(_, newPage) => setBatchPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setBatchRowsPerPage(parseInt(e.target.value as string, 10));
                            setBatchPage(0);
                        }}
                        totalPages={Math.ceil((batchDetails?.total || 0) / batchRowsPerPage)}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AutoSweepTransactionsTable;